'use client';

import { Suspense, useEffect, useLayoutEffect, type ReactNode } from 'react';
import { useParams, usePathname } from 'next/navigation';
import { AuthProvider } from '@/contexts/auth';
import { ApiProvider, type ApiProviderProps } from '@/contexts/api';
import { ClarityProvider } from '@/contexts/clarity';
import { MixpanelProvider, MixpanelPageTracker } from '@/contexts/mixpanel';
import { CgpProvider, CgpPageTracker, useCgp } from '@/contexts/cgp';
import { ToastProvider } from '@/contexts/toast';
import { TooltipProvider } from '@workspace/ui/components/tooltip';
import { CookieConsentProvider, ConsentPresentationSync } from '@/contexts/consent';
import { DatadogConsentSync } from '@/lib/datadog/consent-sync';
import { GoogleTagManager } from '@/lib/gtm';
import { getHomeRequestHeaders, getProductRequestHeaders } from '@/lib/utm';
import { apiRetryEvents, dispatchApiHttpEvent, FINAL_HTTP_ERROR_EVENT } from '@/lib/http-events';
import { ThemeProvider, useTheme } from '@/contexts/theme';
import { AnalyticsIdentitySync } from '@/contexts/app/identity-sync';
import { useAppPolicy } from '@/contexts/app/use-app-policy';
import { toRouteTemplate } from '@/contexts/app/page-path';
import type { AppPolicy } from '@/contexts/app/policy';
import { isInitialContentRoute, markInitialContentReady } from '@/lib/initial-content';

function MixpanelWithMirror({ children }: { children: ReactNode }) {
    const { track } = useCgp();
    return <MixpanelProvider eventMirror={track}>{children}</MixpanelProvider>;
}

/** Consent UI 跟随实际展示主题；区域策略只决定强制值，不复制一份主题状态。 */
function ConsentSurface({ locale }: { locale: string }) {
    const { resolvedTheme } = useTheme();
    return <ConsentPresentationSync locale={locale} theme={resolvedTheme} />;
}

/**
 * 根布局直接挂载；不读取 I18n 子树、Auth 或业务数据。
 * 主题、Consent 与 SDK 各只有这一处装配，所有区域共用同一实例，不按区域重复初始化。
 */
export function Providers({ children, locale }: {
    /** 应用所有路由，不等待遥测就绪。 */
    children: ReactNode;
    /** 根 SSR 已解析的初始语言。 */
    locale: string;
}) {
    const policy = useAppPolicy();
    const pathname = usePathname();
    useEffect(() => {
        // 离开 Workspace（例如准入转去 Waitlist）即结束首次等待；之后软导航不重新初始化 SDK。
        if (!isInitialContentRoute(pathname ?? '')) markInitialContentReady();
    }, [pathname]);
    // 首帧脚本已按路径写好；软导航跨区域时由这里唯一同步，按区域生效的样式随之切换。
    useLayoutEffect(() => {
        document.documentElement.setAttribute('data-enter-area', policy.area);
    }, [policy.area]);
    return (
        <ThemeProvider forcedTheme={policy.forcedTheme} storageKey={policy.themeStorageKey}>
            <CookieConsentProvider locale={locale} theme={policy.forcedTheme ?? 'light'}>
                <ConsentSurface locale={locale} />
                <DatadogConsentSync />
                <ClarityProvider>
                    <CgpProvider>
                        <MixpanelWithMirror>{children}</MixpanelWithMirror>
                    </CgpProvider>
                </ClarityProvider>
                <GoogleTagManager />
            </CookieConsentProvider>
        </ThemeProvider>
    );
}

function RouteTemplatePageViews() {
    const pathname = usePathname() ?? '/';
    const params = useParams() ?? {};
    const path = toRouteTemplate(pathname, params);
    return <><MixpanelPageTracker path={path} /><CgpPageTracker path={path} /></>;
}

/** 计页口径由区域策略决定；公开区沿用 tracker 默认的完整地址口径。 */
function PageViews({ mode }: { mode: AppPolicy['pageViews'] }) {
    if (mode === 'none') return null;
    return (
        <Suspense fallback={null}>
            {mode === 'route-template' ? <RouteTemplatePageViews /> : <><MixpanelPageTracker /><CgpPageTracker /></>}
        </Suspense>
    );
}

/** 归因请求头按区域选择；切换区域只换读取函数，API 客户端不重建。 */
const REQUEST_ATTRIBUTION = {
    registration: getProductRequestHeaders,
    immediate: getHomeRequestHeaders,
} satisfies Record<AppPolicy['requestAttribution'], ApiProviderProps['getAttributionHeaders']>;

const APPLICATION_HTTP_EVENTS = {
    ...apiRetryEvents,
    onFinalHttpError: (error) => dispatchApiHttpEvent(FINAL_HTTP_ERROR_EVENT, error),
} satisfies NonNullable<ApiProviderProps['httpEvents']>;

/**
 * 公开区与产品区共用这一份：Auth、API、身份桥、Toast、Tooltip 与页面统计。
 * 主题与 SDK 实例已在根部，软导航跨区域时不重建。
 */
export function ApplicationProviders({ children, getInviteCode }: {
    /** 区域内的页面内容。 */
    children: ReactNode;
    /** App 注入的 Risk 邀请码读取，不跨服务端序列化函数。 */
    getInviteCode?: () => string | undefined;
}) {
    const { pageViews, loginReturnTo, requestAttribution } = useAppPolicy();
    return (
        <AuthProvider getInviteCode={getInviteCode} returnTo={loginReturnTo}>
            <ApiProvider getAttributionHeaders={REQUEST_ATTRIBUTION[requestAttribution]} httpEvents={APPLICATION_HTTP_EVENTS}>
                <AnalyticsIdentitySync />
                <ToastProvider>
                    <TooltipProvider>{children}</TooltipProvider>
                </ToastProvider>
                <PageViews mode={pageViews} />
            </ApiProvider>
        </AuthProvider>
    );
}
